'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { ArrowUpDown, ChevronDown, Check } from 'lucide-react';
import Transition from '@/components/ui/Transition';
import { useResultsNavigation } from '@/components/filters/use-results-navigation';

interface SortDropdownProps {
  lang: string;
  /** 'bar': one of the three buttons over a list of results (components/results/ResultsBar.tsx). */
  variant?: 'default' | 'bar';
}

export default function SortDropdown({ lang, variant = 'default' }: SortDropdownProps) {
  const bar = variant === 'bar';
  const searchParams = useSearchParams();
  const navigate = useResultsNavigation();
  const [isOpen, setIsOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const isFr = lang === 'fr';

  const currentValue = searchParams?.get('sort') || 'newest';

  const options = [
    { value: 'newest', label: isFr ? 'Plus récents' : 'Newest', short: isFr ? 'Récents' : 'Newest' },
    { value: 'price_asc', label: isFr ? 'Prix croissant' : 'Price: Low to High', short: isFr ? 'Prix ↑' : 'Price ↑' },
    { value: 'price_desc', label: isFr ? 'Prix décroissant' : 'Price: High to Low', short: isFr ? 'Prix ↓' : 'Price ↓' },
    { value: 'photos_desc', label: isFr ? 'Plus de photos' : 'Most photos', short: 'Photos' },
  ];

  const currentOption = options.find((opt) => opt.value === currentValue) || options[0];

  const handleSortChange = (val: string) => {
    setIsOpen(false);
    
    if (val === currentValue) return;

    const params = new URLSearchParams(searchParams?.toString() || '');
    if (val && val !== 'newest') {
      params.set('sort', val);
    } else {
      params.delete('sort');
    }
    // A new order starts at its beginning; page 7 of another order means nothing.
    params.delete('page');

    navigate(params, 'push');
  };

  // Close dropdown on outside click or Escape, and return focus to the
  // trigger so keyboard users don't lose their place.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div className={`relative ${bar ? 'flex-1 sm:flex-none' : ''}`} ref={dropdownRef}>
      <button
        ref={triggerRef}
        onClick={() => {
          // On a phone the bottom bar covers what hangs below a button that sits low on the screen: open upward then.
          const r = triggerRef.current?.getBoundingClientRect();
          setOpenUp(Boolean(r && window.innerWidth < 768 && r.bottom + 280 > window.innerHeight - 90 && r.top > 290));
          setIsOpen(!isOpen);
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        // A phone shows the icon alone: the three owner tabs and this share one
        // row, and the label took a third of it.
        aria-label={`${isFr ? 'Trier' : 'Sort'}: ${currentOption.label}`}
        className={bar
          ? `w-full sm:w-auto h-11 flex items-center justify-center gap-1.5 px-3 rounded-xl border border-neutral-800 bg-neutral-900 text-sm font-semibold text-neutral-100 cursor-pointer whitespace-nowrap`
          : `flex items-center justify-between gap-2 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-bold transition-all duration-200 cursor-pointer min-w-0 sm:min-w-[140px] bg-neutral-950 text-neutral-200 shadow-sm border border-neutral-900 hover:text-champagne hover:border-neutral-800`}
      >
        {bar ? (
          <>
            {/* What the list is sorted by, not the word "Sort": a smaller
                button than Filters (the two are easy to confuse when they look
                alike), and it says what it will change. */}
            <ArrowUpDown aria-hidden="true" className="w-4 h-4 shrink-0" />
            <span className="sm:hidden">{currentOption.short}</span>
            <span className="hidden sm:inline truncate">{currentOption.label}</span>
            <ChevronDown aria-hidden="true" className={`w-4 h-4 shrink-0 text-neutral-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
          </>
        ) : (
          <>
            <ArrowUpDown className="w-4 h-4 sm:hidden" />
            <span className="hidden sm:inline truncate">{currentOption.label}</span>
            <ChevronDown className={`hidden sm:block w-4 h-4 text-neutral-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

          <Transition
            show={isOpen}
            from={{ opacity: 0, y: 8, scale: 0.98 }}
            duration={150}
            role="listbox"
            aria-label={isFr ? 'Trier par' : 'Sort By'}
            // Over a list the button sits in the middle of three: the menu is centred
            // under it rather than hung from its right edge, off the screen.
            className={`absolute ${openUp ? 'bottom-full mb-2' : 'top-full mt-2'} ${bar ? 'left-1/2 -ml-[110px] sm:left-auto sm:ml-0 sm:right-0' : 'right-0'} w-[220px] bg-neutral-900 border border-neutral-800 rounded-2xl shadow-2xl overflow-hidden z-50 py-1.5`}
          >
            <div className="px-3 py-2 border-b border-neutral-800/50 mb-1">
              <span className="text-xs font-mono font-bold text-neutral-400 uppercase tracking-wider">
                {isFr ? 'Trier par' : 'Sort By'}
              </span>
            </div>
            <div className="flex flex-col px-1.5">
              {options.map((opt) => {
                const isActive = opt.value === currentValue;
                return (
                  <button
                    key={opt.value}
                    onClick={() => handleSortChange(opt.value)}
                    role="option"
                    aria-selected={isActive}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-bold transition-colors cursor-pointer w-full text-left ${
                      isActive
                        ? 'bg-neutral-800 text-champagne'
                        : 'text-neutral-300 hover:bg-neutral-800/50 hover:text-white'
                    }`}
                  >
                    <span className="truncate">{opt.label}</span>
                    {isActive && <Check className="w-3.5 h-3.5" />}
                  </button>
                );
              })}
            </div>
          </Transition>
    </div>
  );
}
